import React from "react";
import * as m from "../../lib/model";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, Legend } from "recharts";
import { fmtBig, entryMult, filled, DECK_GREEN, DECK_GOLD } from "./deck";

const TierTick = ({ x, y, payload, index }) => {
  const left = x - 196;
  return (
    <g>
      <rect x={left} y={y - 5} width={9} height={9} rx={2} fill={DECK_GREEN} fillOpacity={[1, 0.62, 0.38][index] ?? 0.38} />
      <text x={left + 17} y={y} dy={4} textAnchor="start" fill="var(--text2)" fontSize={11}>{payload.value}</text>
    </g>
  );
};

export function ValuationBlock({ M, block = {}, platformMode }) {
  const entry = entryMult(M), exit = m.coerceNum(M.exitMult), ex = m.exitReturns(M);
  const custom = filled(block.tiers, "label").filter((t) => +t.high > 0);
  if (!(entry > 0) || !(exit > 0)) return null;
  const lift = exit - entry;
  const tiers = custom.length ? custom : [
    { label: platformMode ? "LMM add-on (entry)" : "Owner-operated (entry)", low: +entry.toFixed(1), high: +(entry + 1.5).toFixed(1) },
    { label: "Multi-location platform", low: +Math.max(entry + 0.5, exit - 1).toFixed(1), high: +Math.max(entry + 1.5, exit + 1).toFixed(1) },
    { label: "Strategic / PE-backed", low: +Math.max(entry + 1.5, exit + 1).toFixed(1), high: +Math.max(entry + 3, exit + 3.5).toFixed(1) },
  ];
  const data = [...tiers].reverse().map((t) => ({ name: t.label, low: +t.low, spread: Math.max(0, +t.high - +t.low), high: +t.high }));
  const maxX = Math.ceil(Math.max(...data.map((d) => d.high)) + 1);
  const adds = m.consolidatedAddons(M).filter((c) => +c.ebitda > 0);
  const unitE = Math.round(platformMode && adds.length ? adds.reduce((a, c) => a + +c.ebitda, 0) / adds.length : M.sde || 1e6);
  const unitPrice = adds.length ? adds.reduce((a, c) => a + (+c.price || +c.ebitda * (+c.multiple || 0)), 0) : 0;
  const unitMult = platformMode && unitPrice > 0 ? unitPrice / adds.reduce((a, c) => a + +c.ebitda, 0) : entry;
  const unitLift = exit - unitMult;
  const headline = block.headline || (platformMode
    ? `The multiple-arbitrage math: buy at ${m.fmtX(entry)} single-location multiples, exit at ${m.fmtX(exit)} platform multiples`
    : `Buy at ${m.fmtX(entry)} owner-operator multiples, exit at ${m.fmtX(exit)} to a scaled acquirer`);
  return (
    <div className="ir-gap" data-testid="ir-valuation">
      <div className="ir-deck-head">
        <span className="ir-eyebrow">Valuation landscape</span>
        <h2 className="ir-deck-h sm" data-testid="ir-val-headline">{headline}</h2>
      </div>
      <div className="ir-val-grid">
        <div>
          <div className="ir-deck-sub">EV / EBITDA — by buyer tier</div>
          <ResponsiveContainer width="100%" height={70 + data.length * 62}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap={18}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
              <XAxis type="number" domain={[0, maxX]} tick={{ fontSize: 10.5, fill: "var(--text3)" }} axisLine={false} tickLine={false} label={{ value: "EV / EBITDA", position: "insideBottom", offset: -2, fontSize: 10.5, fill: "var(--text3)" }} />
              <YAxis type="category" dataKey="name" width={200} tick={<TierTick />} axisLine={false} tickLine={false} />
              <Legend verticalAlign="bottom" iconType="square" iconSize={9} wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
              <Bar dataKey="low" name="Low" stackId="a" fill={DECK_GREEN}><LabelList dataKey="low" position="center" formatter={(v) => `${v.toFixed(1)}x`} style={{ fontSize: 11, fill: "var(--bg)" }} /></Bar>
              <Bar dataKey="spread" name="High" stackId="a" fill={DECK_GOLD}><LabelList dataKey="high" position="center" formatter={(v) => `${v.toFixed(1)}x`} style={{ fontSize: 11, fill: "var(--bg)" }} /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mk-panel" data-testid="ir-val-arb">
          <span className="mk-panel-k">The arbitrage</span>
          <div className="mk-stat"><b>{lift > 0 ? `~${lift.toFixed(1)}x` : m.fmtX(exit)}</b><span>{lift > 0 ? "turn multiple lift" : "underwritten exit multiple"}</span></div>
          <div className="mk-panel-body">
            <b className="gold">Illustrative:</b>
            {lift > 0 ? (<>
              <p>{platformMode && adds.length ? `The average add-on: ${fmtBig(unitE)} of EBITDA` : `A ${fmtBig(unitE)} EBITDA business`} bought at {unitMult.toFixed(1)}x = {fmtBig(unitE * unitMult)}</p>
              <p>Rolled into a {fmtBig(ex.sdeExit)}-EBITDA platform at {exit.toFixed(1)}x = {fmtBig(ex.exitEV)} enterprise value{M.holdIndefinite ? " if ever sold (an indefinite hold is underwritten)" : ""}.</p>
              <em>Every incremental $1 of {platformMode ? "add-on " : ""}EBITDA is worth ~${Math.max(0, unitLift).toFixed(2)} more at exit.</em>
            </>) : (<>
              <p>Entry at {entry.toFixed(2)}x versus a conservative {exit.toFixed(2)}x exit assumption — no multiple expansion is underwritten.</p>
              <p>Returns come from cash flow, debt paydown and earnings growth to {fmtBig(ex.sdeExit)} of exit EBITDA{M.holdIndefinite ? " — no sale is underwritten" : ` (${fmtBig(ex.exitEV)} enterprise value)`}.</p>
              <em>Every turn of exit multiple above {exit.toFixed(1)}x is upside to the base case.</em>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}
